import React from 'react';
import { connect } from 'react-redux';
import Highcharts from 'highcharts';
import _ from 'lodash';
import moment from 'moment';

import dateUtils from '../../../util/dates';
import highchartFactory from '../../../util/highcharts';
import chartUtils from '../../../util/chartUtils';
import { PERCENTILE_LABEL_COLORS } from '../../../util/colors';
const highchartUtils = highchartFactory(Highcharts);

const chartConfig = {
  exporting: {
    enabled: false
  },
  credits: {
    enabled: false
  },
  chart: {
    backgroundColor: '#fff',
    type: 'spline'
  },
  title: {
    text: ''
  },
  tooltip: {
    useHTML: true,
    shared: true,
    shadow: false,
    hideDelay: 0,
    borderRadius: 5,
    borderWidth: 0,
    formatter: function() {}
  },
  xAxis: {
    type: 'datetime',
    gridLineWidth: 1,
    showFirstLabel: true,
    showLastTickLabel: true,
    labels: {
      style: {
        fontSize: '12px'
      }
    },
    title: {
      text: null
    }
  },
  yAxis: {
    gridLineWidth: 1,
    labels: {
      enabled: true,
      style: {
        fontSize: '12px'
      }
    },
    title: {
      text: null
    },
    showFirstLabel: true,
    showLastTickLabel: true
  },
  legend: {
    enabled: false
  },
  plotOptions: {
    series: {
      stickyTracking: false
    },
    spline: {
      lineWidth: 1.5,
      marker: {
        symbol: 'circle',
        enabledThreshold: 2,
        lineWidth: 1,
        radius: 3
      },
      states: {
        hover: {
          marker: {
            enabled: false
          }
        }
      }
    }
  },
  drilldown: {
    allowPointDrilldown: false,
    series: []
  },
  series: [
    {
      data: []
    }
  ]
};

const END_LABEL = {
  enabled: true,
  align: 'right',
  verticalAlign: 'middle',
  x: -6,
  allowOverlap: false,
  crop: false,
  backgroundColor: '#FFFFFF',
  borderRadius: 4,
  padding: 3,
  style: {
    color: '#545D66',
    fontSize: '12px',
    fontWeight: '500',
    textOutline: 'none'
  }
};

export function percentileEndLabels(series) {
  return _(series)
    .filter(s => s.data && s.data.length)
    .groupBy(s => _.last(s.data).y)
    .map(group => {
      const highestFirst = _.orderBy(group, s => Number(s.name), 'desc');
      return [highestFirst[0].name, highestFirst.map(s => `${s.name}%`).join(', ')];
    })
    .fromPairs()
    .value();
}

class LineChart extends React.Component {
  static defaultProps = {
    unit: '',
    tooltipLabel: '',
    data: []
  }; 

  componentDidMount() {
    highchartUtils.setDefaults();
    const that = this;
    chartConfig.tooltip.formatter = function() {
      return that.tooltipFormatter(that, this);
    };

    this.chart = new Highcharts['Chart'](`${this.props.id}-details`, chartConfig);
    this.chart.update(chartUtils.trendLineChart(this.props.unit), false);

    this.updateTimeLabelsFormat(this.props.dateFormat);

    if (this.props.data) {
      this.updateChart();
      this.chart.redraw();
    }
  }

  componentDidUpdate(prevProps) {
    if (
      !_.isEqual(prevProps.data, this.props.data) ||
      !_.isEqual(prevProps.detailed, this.props.detailed) ||
      prevProps.showLegend !== this.props.showLegend
    ) {
      this.updateChart();
    }
    if (prevProps.dateFormat !== this.props.dateFormat) {
      this.updateTimeLabelsFormat(this.props.dateFormat);
    }
    this.updateFontSize();                       
  }

  componentWillUnmount() {
    this.chart.destroy();
  }

  updateChart() {
    while (this.chart.series.length > 0) {
      this.chart.series[0].remove(false);
    }

    if (!this.props.data.length) {
      return false;
    }

    const detailsEnabled = typeof this.props.detailed === 'boolean';
    const legendEnabled = this.props.showLegend || (detailsEnabled && this.props.detailed);
    const legendSuffix = this.props.legendSuffix;
    const legendOptions = {
      enabled: legendEnabled,
      ...(legendEnabled ? chartUtils.cardLegend(this.props.isFullScreen) : {}),
      ...(legendEnabled && legendSuffix ? { labelFormatter: function() { return this.name + legendSuffix; } } : {})
    };
    this.chart.update({ legend: legendOptions });

    const shown = _.filter([...this.props.data], (s, i) =>
      !detailsEnabled || (this.props.detailed ? i > 0 : i === 0)
    );
    const ownColors = this.props.keepColors || (detailsEnabled && this.props.detailed);
    const endLabels = this.props.id === 'percentiles-trend' ? percentileEndLabels(shown) : {};
    _.forEach(shown, s => {
      const color = ownColors ? s.color : chartUtils.TREND_LINE_COLOR;
      const label = endLabels[s.name];
      const data = label ? [..._.initial(s.data), { ..._.last(s.data), dataLabels: { ...END_LABEL, format: label, style: { ...END_LABEL.style, color: PERCENTILE_LABEL_COLORS[s.name] } } }] : s.data;
      const series = { ...s, color, data };
      const line = chartUtils.trendLineSeries(series, !ownColors && shown.length === 1);
      // the legend draws an area series as a dot in its colour, and a line series as a line that _diagram.scss hides
      this.chart.addSeries(legendEnabled ? { ...line, type: 'areaspline', fillOpacity: 0 } : line, false);
    });

  }

  updateFontSize = () => {
    const opt = {
      exporting: {
        enabled: this.props.isFullScreen
      },
      plotOptions: {
        spline: {
          dataLabels: {
            style: {
              fontSize: this.props.isFullScreen ? '16px' : '11px',
              fontWeight: 'bold',
              color: 'contrast'
            }
          }
        }
      }
    };

    this.chart.update(opt);
    this.chart.reflow();
  };

  updateTimeLabelsFormat(dateFormat) {
    let config = { xAxis: {} };
    config.xAxis.dateTimeLabelFormats = dateUtils.createHighchartFormats(dateFormat);

    this.chart.update(config);
  }

  tooltipFormatter(comp, ev) {
    if (comp.props.id === 'percentiles-trend') {
      let tooltipHtml = '',
        s = [];
      _(ev.points)
        .orderBy(p => p.series.name, 'desc')
        .forEach(point => {
          tooltipHtml += `<div class="tooltip-label-item"><span class="label-box" style="background: ${point.color}"></span>${point.series.name}%: ${point.point.y} day${point.point.y != 1 ? 's' : ''}</div>`;
        });
      s.push(tooltipHtml);
      return s;
    }

    const unit = 'month';
    const date = `${moment(ev.x)
      .startOf(unit)
      .format(this.props.dateFormat)} - ${moment(ev.x)
      .endOf(unit)
      .format(this.props.dateFormat)}`;

    let tooltip = `<p style="font-size: 12px; margin: 0">Date: ${this.props.showExactDate ? moment(ev.x).format(this.props.dateFormat): date}</p>`;
    if (!comp.props.detailed) {
      const value = `${ev.y} ${comp.props.unit}${comp.props.plural && ev.y != 1 ? 's' : ''}`;
      tooltip += `<p style="margin: 4px 0 0; font-size: 12px;">${
        !!comp.props.tooltipLabel ? `${comp.props.tooltipLabel}: ` : ''
      }${value}</p>`;
    } else {
      _.forEach(ev.points, point => {
        const v = `${point.point.y} ${comp.props.unit}${comp.props.plural && point.point.y != 1 ? 's' : ''}`;
        tooltip += `<div class="tooltip-label-item"><span class="label-box" style="background: ${point.color}"></span>${point.series.name}: ${v}</div>`;
      });
    }

    return tooltip;
  }

  render() {
    return (
      <div id={this.props.id} className={this.props.className}>
        <div className="chart_tooltip_style">
          <div id={`${this.props.id}-details`}></div>
        </div>
        {/* like the chart cards (3190 EX 5): the line drawn pale while the data loads, "No data available" without data */}
        {(this.props.loading || !chartUtils.hasValues(this.props.data)) && (
          <div
            className={`chart-placeholder chart-placeholder--line ${this.props.loading ? 'chart-placeholder--loading' : ''}`}
            dangerouslySetInnerHTML={{ __html: chartUtils.placeholderMarkup('line', this.props.loading) }}
          />
        )}
      </div>
    );
  }
}

const mapStateToProps = state => {
  return {
    filters: state.report.filters,
    dateFormat: state.filters.dateFormat
  };
};

export default connect(mapStateToProps)(LineChart);
